/**
 * All wall-clock dates/times in the UI render in California time
 * (America/Los_Angeles, which auto-switches between PST/PDT). The dashboard
 * is for a single California institution, so users want to see times in
 * their own timezone even when their browser is set elsewhere.
 *
 * Use `TZ` for every Intl.DateTimeFormat / toLocale* call that produces a
 * user-visible string. Anything timezone-independent (relative durations,
 * "Xm ago") needs no change.
 */
const TZ = "America/Los_Angeles";

/** "6:05 PM" or "6:05:17 PM" — wall-clock time in PT. */
function formatTimeInTz(d: Date, withSeconds: boolean): string {
  return d.toLocaleTimeString("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: true,
  });
}

/** "May 21" or "May 21, 2025" — calendar date in PT. */
function formatDateInTz(d: Date, includeYear: boolean): string {
  return d.toLocaleDateString("en-US", {
    timeZone: TZ,
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
}

/** Stable YYYY-MM-DD key for the date *in PT* — used to group/compare days. */
function ptDateKey(d: Date): string {
  // en-CA gives YYYY-MM-DD, which is what we want for a stable map key.
  return d.toLocaleDateString("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** Calendar year *in PT* — used for "include year only when not the current year". */
function ptYear(d: Date): number {
  return Number(
    d.toLocaleDateString("en-US", { timeZone: TZ, year: "numeric" }),
  );
}

/**
 * Friendly duration like "5m 32s", "1h 5m", "32s", "<1s".
 * Returns "—" for undefined / zero / negative values.
 */
export function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms <= 0) return "—";
  if (ms < 1000) return "<1s";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  }
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Compact relative time. ≤24h is relative ("3m ago"); 1–6 days is "Xd ago";
 * older switches to an absolute date so the user can tell at a glance how
 * old the row is without doing arithmetic in their head.
 */
export function formatRelative(iso: string | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  if (diff < 5_000) return "now";
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return formatShortDate(iso);
}

/** "May 21" (same PT year) or "May 21, 2024" (different PT year). */
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const includeYear = ptYear(d) !== ptYear(new Date());
  return formatDateInTz(d, includeYear);
}

/** "2:30:45 PM" — local PT wall-clock time. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return formatTimeInTz(d, true);
}

/**
 * Friendly absolute date+time in PT. Switches between four formats:
 *   today (PT)    → "Today at 2:30 PM"
 *   day -1 (PT)   → "Yesterday at 2:30 PM"
 *   same PT year  → "May 21 at 2:30 PM"
 *   older         → "May 21, 2025 at 2:30 PM"
 */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";

  const now = new Date();
  const todayKey = ptDateKey(now);
  const yesterdayKey = ptDateKey(new Date(now.getTime() - 86_400_000));
  const itemKey = ptDateKey(d);
  const time = formatTimeInTz(d, false);

  if (itemKey === todayKey) return `Today at ${time}`;
  if (itemKey === yesterdayKey) return `Yesterday at ${time}`;

  const includeYear = ptYear(d) !== ptYear(now);
  return `${formatDateInTz(d, includeYear)} at ${time}`;
}

export interface DateGroup<T> {
  /** Stable React key: `today` | `yesterday` | `YYYY-MM-DD` (in PT). */
  key: string;
  /** Primary header text: `Today` | `Yesterday` | `Wednesday, May 20, 2026`. */
  label: string;
  /** Secondary text — the explicit date for "Today" / "Yesterday". */
  sublabel?: string;
  items: T[];
}

/**
 * Bucket items by calendar day **in California time**, preserving the
 * caller's relative order within each bucket. Items whose timestamp
 * accessor returns `undefined` or unparseable are dropped — the Runs list
 * page is sorted by `queuedAt`, which is always populated.
 *
 * Today and Yesterday get friendly labels with the explicit date as a
 * sublabel; every older day gets one bucket each, labelled with the full
 * weekday + date (so the user can always tell at a glance which day they
 * are looking at).
 *
 * Group order falls out of insertion order: the input is expected to be
 * DESC by timestamp, so groups land in DESC order automatically.
 */
export function groupByDate<T>(
  items: T[],
  getIso: (item: T) => string | undefined,
): DateGroup<T>[] {
  if (items.length === 0) return [];

  const now = new Date();
  const todayKey = ptDateKey(now);
  const yesterdayKey = ptDateKey(new Date(now.getTime() - 86_400_000));

  const groups = new Map<string, DateGroup<T>>();

  for (const item of items) {
    const iso = getIso(item);
    if (!iso) continue;
    const date = new Date(iso);
    if (!Number.isFinite(date.getTime())) continue;

    const itemKey = ptDateKey(date);

    let key: string;
    let label: string;
    let sublabel: string | undefined;

    if (itemKey === todayKey) {
      key = "today";
      label = "Today";
      sublabel = date.toLocaleDateString("en-US", {
        timeZone: TZ,
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } else if (itemKey === yesterdayKey) {
      key = "yesterday";
      label = "Yesterday";
      sublabel = date.toLocaleDateString("en-US", {
        timeZone: TZ,
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } else {
      key = itemKey; // YYYY-MM-DD in PT — stable across renders.
      label = date.toLocaleDateString("en-US", {
        timeZone: TZ,
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    let group = groups.get(key);
    if (!group) {
      group = { key, label, sublabel, items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }

  return Array.from(groups.values());
}

/**
 * Try to parse a Canvas-Sync ID (runId / uploadId) into a Date. The IDs are
 * just ISO timestamps with dashes replacing the colons and dot, e.g.
 *   run-2026-05-21T18-05-17-339Z   →  2026-05-21T18:05:17.339Z
 *   upload-2026-05-21T18-05-17-339Z →  2026-05-21T18:05:17.339Z
 * Returns null if the ID doesn't carry an embedded timestamp.
 */
export function parseIdTimestamp(id: string | undefined): Date | null {
  if (!id) return null;
  const m = id.match(/(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z/);
  if (!m) return null;
  const iso = `${m[1]}T${m[2]}:${m[3]}:${m[4]}.${m[5]}Z`;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Friendly display label for a sync/upload ID — renders the embedded UTC
 * timestamp as PT wall-clock time, e.g. "May 21 at 6:05:17 PM". Includes
 * seconds so two IDs from the same minute don't collide on screen (the
 * second is the discriminator users have for support tickets). Falls back
 * to the raw ID if it isn't a timestamped one.
 */
export function formatIdLabel(id: string): string {
  const d = parseIdTimestamp(id);
  if (!d) return id;
  const includeYear = ptYear(d) !== ptYear(new Date());
  const date = formatDateInTz(d, includeYear);
  const time = formatTimeInTz(d, true);
  return `${date} at ${time}`;
}

/**
 * Friendly display label for a SIS bundle `dropId`, rendered as PT wall-clock
 * like "May 26 at 6:55:00 AM" — the same style as `formatIdLabel` for
 * run/upload IDs. Falls back to the raw dropId if it isn't a timestamp.
 *
 * Production dropIds are the n8n pull time in the SAME shape as run/upload
 * IDs: `YYYY-MM-DDTHH-MM-SS-mmmZ` (e.g. "2026-05-26T13-55-00-000Z"), built by
 * `$now.toUTC().toISO().replaceAll(/[:.]/g, '-')` — so `parseIdTimestamp`
 * already handles them. We also tolerate the shortened
 * `YYYY-MM-DDTHH-MM-SS` form (no millis/Z) that the shared docs + some test
 * fixtures use. (An earlier version only matched the short form, so real
 * dropIds fell through and rendered raw — the bug this comment guards.)
 */
export function formatDropId(dropId: string): string {
  if (parseIdTimestamp(dropId)) return formatIdLabel(dropId);
  const m = dropId.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})$/);
  if (!m) return dropId;
  const d = new Date(`${m[1]}T${m[2]}:${m[3]}:${m[4]}Z`);
  if (!Number.isFinite(d.getTime())) return dropId;
  const includeYear = ptYear(d) !== ptYear(new Date());
  return `${formatDateInTz(d, includeYear)} at ${formatTimeInTz(d, true)}`;
}

/** "6:05 PM" — wall-clock PT time, useful for narrow columns. */
export function formatIdShortTime(id: string): string {
  const d = parseIdTimestamp(id);
  if (!d) return id;
  return formatTimeInTz(d, false);
}

/** Human-readable byte size: "812 B", "4.2 KB", "1.3 MB", "2.0 GB". */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Best-effort short-render of an unknown metadata value. */
export function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined) return String(v);
  if (typeof v === "string") {
    if (v.length > 100) return JSON.stringify(v.slice(0, 97) + "…");
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    if (v.length <= 3) return JSON.stringify(v);
    return `[${v.length} items]`;
  }
  return JSON.stringify(v);
}
